import React, { FC, useRef, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
} from '@chakra-ui/react';

import { HiDocumentAdd } from 'react-icons/hi';
import { FaSort } from 'react-icons/fa';
import { MdClose, MdSearch } from 'react-icons/md';
import { format } from 'date-fns';

import { UserPill } from 'components/core/UserPill';
import { CustomTable } from 'components/modules/CustomTable';
import {
  CreatePurchaseOrderModal,
  CreatePurchaseOrderModalRef,
} from 'components/modules/CreatePurchaseOrderModal';
import { PurchaseOrderAttachmentsModal } from 'components/modules/PurchaseOrderAttachmentsModal';
import { PurchaseOrderDetailsModal } from 'components/modules/PurchaseOrderDetailsModal';
import { MarkPurchaseOrderReceivedModal } from 'components/modules/MarkPurchaseOrderReceivedModal';
import { PayPurchaseOrderModal } from 'components/modules/PayPurchaseOrderModal';
import { PurchaseOrderFilters } from 'components/modules/PurchaseOrderFilters';
import {
  PURCHASE_ORDER_STATUS_TO_ACTION,
  PURCHASE_ORDER_STATUS_TO_LABEL,
} from 'constants/purchaseOrder';
import { useRoles } from 'store/slices/auth/authSelectors';
import { PurchaseOrder } from 'types/purchaseOrder';
import { RolesEnum } from 'types/user';

import { PurchaseOrdersViewProps } from './PurchaseOrders.props';

const COLUMN_WIDTHS = {
  poNumber: '80px',
  date: '120px',
  manufacturer: '180px',
  destinationHub: '180px',
  orderDescription: '400px',
  status: '100px',
  createdAt: '150px',
  lastUpdate: '150px',
  actionButton: '200px',
  attachPdf: '140px',
  notes: '200px',
} as const;

const TABLE_MIN_WIDTH = '1950px';

const columnCellProps = (width: string) => ({
  w: width,
  minW: width,
  maxW: width,
  flexShrink: 0,
});

const formatDate = (value?: string) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, 'MMMM d, yyyy');
};

const formatPurchaseOrderTableDate = (purchaseOrder: PurchaseOrder) => {
  const value = purchaseOrder.date || purchaseOrder.createdAt;

  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return format(date, 'MM-dd-yyyy');
};

const PurchaseOrdersView: FC<PurchaseOrdersViewProps> = ({
  purchaseOrders: _purchaseOrders,
  isLoading: _isLoading,
  pagination: _pagination,
  filters,
  search,
  setSearch,
  defaultIndex,
  setDefaultIndex,
  counts,
  onSort,
  onApplyFilters,
  onClearFilters,
}) => {
  const [
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
  ] = useState<PurchaseOrder>();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [isPayOpen, setIsPayOpen] = useState(false);
  const createPurchaseOrderModalRef = useRef<CreatePurchaseOrderModalRef>(null);
  const roles = useRoles();
  const canCreatePurchaseOrder = Boolean(
    roles?.some(
      (permission) =>
        permission.role === RolesEnum.WASH_HUB_ADMIN ||
        permission.role === RolesEnum.SUPER_ADMIN,
    ),
  );

  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const _selected = { backgroundColor: '#83FF9C80', color: '#075015' };

  const openDetails = (purchaseOrder: PurchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsDetailsOpen(true);
  };

  const openAttachments = (purchaseOrder: PurchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsAttachmentsOpen(true);
  };

  const openReceive = (purchaseOrder: PurchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsReceiveOpen(true);
  };

  const openPay = (purchaseOrder: PurchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsPayOpen(true);
  };

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Purchase Orders
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            <Button minW="200px">Export Purchase Orders to CSV</Button>
            {canCreatePurchaseOrder && (
              <Button
                mr={2}
                minW="200px"
                leftIcon={<HiDocumentAdd size="20px" />}
                onClick={() => createPurchaseOrderModalRef.current?.onOpen()}>
                Create Purchase Order
              </Button>
            )}
            <UserPill />
          </Stack>
        </Flex>
      </Flex>

      <Flex align="flex-start" justify="" wrap="wrap" gap={2}>
        <PurchaseOrderFilters
          values={filters}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
        />
        <Stack spacing={1} ml={2} minW="240px" maxW="280px" w="100%">
          <InputGroup>
            <Input
              placeholder="Enter PO number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <InputRightElement>
              {search ? (
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="clear-search"
                  icon={<MdClose size="18px" />}
                  onClick={() => setSearch('')}
                />
              ) : (
                <MdSearch fill="#2FB64A" />
              )}
            </InputRightElement>
          </InputGroup>
        </Stack>
      </Flex>

      <Tabs
        variant="enclosed"
        size="md"
        index={defaultIndex || 0}
        defaultIndex={defaultIndex}
        onChange={(item) => setDefaultIndex(item)}>
        <TabList _selected={_selected} _focus={_focus} w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All {`(${counts.all})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Active {`(${counts.active})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Completed {`(${counts.completed})`}
          </Tab>
        </TabList>
      </Tabs>

      <CustomTable
        mt={6}
        data={_purchaseOrders}
        minW={TABLE_MIN_WIDTH}
        isLoading={_isLoading}
        pagination={_pagination}
        isSelectable={false}>
        {{
          headerRow: (
            <>
              <Text {...columnCellProps(COLUMN_WIDTHS.poNumber)}>PO#</Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.date)}>Date</Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.manufacturer)}>
                Manufacturer
              </Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.destinationHub)}>
                Destination Hub
              </Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.orderDescription)}>
                Order Description
              </Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.status)}>Status</Text>
              <Text {...columnCellProps(COLUMN_WIDTHS.actionButton)} />
              <Flex
                {...columnCellProps(COLUMN_WIDTHS.createdAt)}
                align="center">
                <Button
                  onClick={() => onSort('createdAt')}
                  variant="plain"
                  color="support.500"
                  background="transparent"
                  alignItems="flex-start"
                  textStyle="xs"
                  fontSize={13}
                  padding="0"
                  pl="10px">
                  Created At <FaSort />
                </Button>
              </Flex>
              <Flex
                {...columnCellProps(COLUMN_WIDTHS.lastUpdate)}
                align="center">
                <Button
                  onClick={() => onSort('updatedAt')}
                  variant="plain"
                  color="support.500"
                  background="transparent"
                  alignItems="flex-start"
                  textStyle="xs"
                  fontSize={13}
                  padding="0"
                  pl="10px">
                  Last Update <FaSort />
                </Button>
              </Flex>
              <Text {...columnCellProps(COLUMN_WIDTHS.attachPdf)} />
              <Text
                style={{ paddingLeft: 10 }}
                {...columnCellProps(COLUMN_WIDTHS.notes)}>
                Receiving Notes
              </Text>
            </>
          ),
          renderRow: (purchaseOrder) => (
            <>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.poNumber)}
                onClick={() => openDetails(purchaseOrder)}>
                {purchaseOrder.poNumer}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.date)}
                onClick={() => openDetails(purchaseOrder)}>
                {formatPurchaseOrderTableDate(purchaseOrder)}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.manufacturer)}
                onClick={() => openDetails(purchaseOrder)}>
                {purchaseOrder.manufacturer?.name}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.destinationHub)}
                onClick={() => openDetails(purchaseOrder)}>
                {purchaseOrder.location}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.orderDescription)}
                whiteSpace="pre-line"
                onClick={() => openDetails(purchaseOrder)}>
                {purchaseOrder.orderDetails
                  ?.map(
                    (orderDetail) =>
                      `${orderDetail.itemQuantity} items (${orderDetail.boxQuantity} boxes) x ${orderDetail.art?.number} ${orderDetail.sku?.name}`,
                  )
                  .join('\n')}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.status)}
                onClick={() => openDetails(purchaseOrder)}>
                {
                  PURCHASE_ORDER_STATUS_TO_LABEL[
                    purchaseOrder.status as keyof typeof PURCHASE_ORDER_STATUS_TO_LABEL
                  ]
                }
              </Text>
              <Flex
                {...columnCellProps(COLUMN_WIDTHS.actionButton)}
                align="center"
                justify="center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (purchaseOrder.status === 'open') {
                      openDetails(purchaseOrder);
                    }

                    if (
                      purchaseOrder.status === 'sent' ||
                      purchaseOrder.status === 'partiallyReceived'
                    ) {
                      openReceive(purchaseOrder);
                    }

                    if (purchaseOrder.status === 'received') {
                      openPay(purchaseOrder);
                    }
                  }}>
                  {
                    PURCHASE_ORDER_STATUS_TO_ACTION[
                      purchaseOrder.status as keyof typeof PURCHASE_ORDER_STATUS_TO_ACTION
                    ]
                  }
                </Button>
              </Flex>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.createdAt)}
                onClick={() => openDetails(purchaseOrder)}
                style={{ paddingLeft: 10 }}>
                {formatDate(purchaseOrder.createdAt)}
              </Text>
              <Text
                {...columnCellProps(COLUMN_WIDTHS.lastUpdate)}
                onClick={() => openDetails(purchaseOrder)}
                style={{ paddingLeft: 10 }}>
                {formatDate(purchaseOrder.updatedAt)}
              </Text>
              <Flex
                {...columnCellProps(COLUMN_WIDTHS.attachPdf)}
                align="center"
                justify="center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(event) => {
                    event.stopPropagation();
                    openAttachments(purchaseOrder);
                  }}>
                  Attachments
                </Button>
              </Flex>
              <Text
                style={{ paddingLeft: 10 }}
                {...columnCellProps(COLUMN_WIDTHS.notes)}
                onClick={() => openDetails(purchaseOrder)}>
                {purchaseOrder.receivingNotes}
              </Text>
            </>
          ),
          renderEmpty: () => (
            <Center p={8}>
              <Text fontSize="20px">No Purchase Orders available</Text>
            </Center>
          ),
        }}
      </CustomTable>

      <CreatePurchaseOrderModal ref={createPurchaseOrderModalRef} />
      <PurchaseOrderDetailsModal
        isOpen={isDetailsOpen}
        purchaseOrder={selectedPurchaseOrder}
        onClose={() => setIsDetailsOpen(false)}
        onViewAttachments={() => setIsAttachmentsOpen(true)}
        onPurchaseOrderUpdated={setSelectedPurchaseOrder}
        onMarkAsReceived={() => setIsReceiveOpen(true)}
        onPayVendor={() => setIsPayOpen(true)}
      />
      <PurchaseOrderAttachmentsModal
        isOpen={isAttachmentsOpen}
        purchaseOrder={selectedPurchaseOrder}
        onClose={() => setIsAttachmentsOpen(false)}
        onPurchaseOrderUpdated={setSelectedPurchaseOrder}
      />
      <MarkPurchaseOrderReceivedModal
        isOpen={isReceiveOpen}
        purchaseOrder={selectedPurchaseOrder}
        onClose={() => setIsReceiveOpen(false)}
        onPurchaseOrderUpdated={setSelectedPurchaseOrder}
      />
      <PayPurchaseOrderModal
        isOpen={isPayOpen}
        purchaseOrder={selectedPurchaseOrder}
        onClose={() => setIsPayOpen(false)}
        onPurchaseOrderUpdated={setSelectedPurchaseOrder}
      />
    </Flex>
  );
};

export default PurchaseOrdersView;
